import { programs as programsApi } from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import { useLoadingIds } from '@hooks/useLoadingIds'
import { useShowOnDesktop } from '@hooks/useShowOnDesktop'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import {
  Box,
  Card,
  CircularProgress,
  Paper,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import {
  PotentialInvitePreviousProgram,
  ProgramInvite,
  ProgramInviteFamilyOptionsResponsePrograms,
} from '@swagger'
import { dateToDashString } from '@utils/dateUtility'
import { reinterpretYearMonthDayAsLocalTime } from '@utils/reinterpretYearMonthDayAsLocalTime'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useLoadingContext from '../../hooks/useLoadingContext'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import IconTextButton, {
  IconTextButtonVariant,
} from '../Buttons/IconTextButton'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'
import { FormDivider } from '../Elements/FormDivider'
import Header, { HeaderVariant } from '../Elements/Header'
import CardFormHeader from './CardFormHeader'
import InviteFamilyProgramCapacityContentCard from './InviteFamilyProgramCapacityContentCard'
import {
  InviteRow,
  InviteRowUpdate,
  StudentInviteRow,
} from './StudentInviteRow'
import { AvailableSpots } from './StudentProgramFormFields/types'

export type ProgramData = ProgramInvite & {
  programType: string
  dateProgram: Date
}
interface InviteReturningFamilyFormCardProps {
  parentFirstName: string
  parentLastName: string
  parentEmail: string
  communityName: string
  communityKey: number
  previousPrograms?: PotentialInvitePreviousProgram[]
  disableSendButton?: boolean
  onCancel: () => void
  onSubmit: (data: { programs: Array<ProgramData> }) => void
}

interface ProgramCapacity extends AvailableSpots {
  outOfSpots: boolean
}

const FormCard = styled(Card)(({ theme }) => ({
  maxWidth: 1200,
  padding: theme.spacing(3, 4),
  color: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 4),
  },
})) as typeof Card

const Form = styled('form')(({ theme }) => ({
  '& .MuiTextField-root': {
    margin: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0),
    },
  },
}))

const SectionOne = styled('section')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '50% 50%',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

const SectionTwo = styled('section')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '50% 25% 25%',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

const ReadOnlyTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(1, 0),
  },
}))

const createInviteRowId = () => `field-${Date.now()}-${Math.random()}`

export const InviteReturningFamilyFormCard: React.FC<
  InviteReturningFamilyFormCardProps
> = ({
  parentFirstName,
  parentLastName,
  parentEmail,
  communityName,
  communityKey,
  previousPrograms = [],
  disableSendButton = false,
  onCancel,
  onSubmit,
}) => {
  const theme = useTheme()
  const { handleError } = useAppSnackbar()
  const { t } = useTranslation()
  const showOnDesktop = useShowOnDesktop()
  const { InviteReturningFamilyFormCard: loadingIds } = useLoadingIds()

  const [programs, setPrograms] = useState<
    ProgramInviteFamilyOptionsResponsePrograms[]
  >([])
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([])
  const hasInitializedRef = useRef(false)

  const defaultFetchInviteFamilyPrograms = t(
    'Families.FamilyForm.FetchInviteFamilyPrograms.Error',
    'Error occurred fetching invite family programs.'
  )

  const fetchPrograms = useCallback(async () => {
    if (!communityKey) return

    try {
      const result = await programsApi.fetchInviteFamilyPrograms({
        communityKey,
      })

      setPrograms(result.programs)
    } catch (e) {
      await handleError(e, defaultFetchInviteFamilyPrograms)
    }
  }, [communityKey, defaultFetchInviteFamilyPrograms, handleError])

  const { triggerFetch, isLoading } = useLoadingContext({
    asyncFunction: fetchPrograms,
    loadingId: loadingIds.fetchInviteFamilyPrograms,
  })

  useEffect(() => {
    triggerFetch()
  }, [communityKey, triggerFetch])

  /**
   * Pre-populate fields from previousPrograms.
   */
  useEffect(() => {
    if (
      programs.length > 0 &&
      previousPrograms.length > 0 &&
      !hasInitializedRef.current
    ) {
      hasInitializedRef.current = true
      const remainingSpots = new Map(
        programs.map((program) => [program.programKey, program.availableSpots])
      )
      const programInvitations: InviteRow[] = previousPrograms.map(
        (previousProgram) => {
          const suggested = previousProgram.suggestedNextProgramType
            ? programs.find(
                (program) =>
                  program.programType ===
                  previousProgram.suggestedNextProgramType
              )
            : undefined

          const fallback = programs.find(
            (program) => program.availableSpots > 0
          )

          const selected = suggested ?? fallback ?? programs[0]

          let isProgramFull = false
          if (selected) {
            const currentSpots = remainingSpots.get(selected.programKey) ?? 0
            remainingSpots.set(selected.programKey, currentSpots - 1) // reduce by one

            if (currentSpots <= 0) {
              isProgramFull = true
              handleError({
                message: 'At least one of your programs has no spots available',
              })
            }
          }

          return {
            id: createInviteRowId(),
            programKey: selected?.programKey ?? null,
            previousProgramType: previousProgram.previousProgramType,
            isApplicationFeePaid: false,
            isProgramFull,
          }
        }
      )

      setInviteRows(programInvitations)
    }
  }, [programs, previousPrograms, handleError])

  /**
   * Centralized capacity calculation.
   *
   * programs contains the original API data.
   * capacityByProgram contains all capacity-related derived state.
   *
   * inviteRows is the source of truth for spots offered in the
   * current invite.
   */
  const capacityByProgram = useMemo(() => {
    const map = new Map<number, ProgramCapacity>()

    programs.forEach((program) => {
      map.set(program.programKey, {
        programKey: program.programKey,
        programType: program.programType,
        spotsOfferedInCurrentInvite: 0,
        availableSpots: program.availableSpots,
        outOfSpots: program.availableSpots <= 0,
        dateProgram: program.semesterOneStartDate,
        dropdownsKeys: [],
      })
    })

    inviteRows.forEach((inviteRow) => {
      if (inviteRow.programKey == null) return

      const capacity = map.get(inviteRow.programKey)

      if (!capacity) return

      capacity.spotsOfferedInCurrentInvite += 1
      capacity.outOfSpots =
        capacity.spotsOfferedInCurrentInvite >= capacity.availableSpots
    })

    return map
  }, [programs, inviteRows])

  /**
   * All aggregate capacity information is derived from
   * capacityByProgram so there is a single source of truth.
   */
  const capacityStats = useMemo(() => {
    const capacities = Array.from(capacityByProgram.values())

    const spotStats = capacities.filter(
      (capacity) => capacity.spotsOfferedInCurrentInvite > 0
    )

    const totalAvailableSpots = capacities.reduce(
      (total, capacity) => total + capacity.availableSpots,
      0
    )

    const totalOfferedSpots = capacities.reduce(
      (total, capacity) => total + capacity.spotsOfferedInCurrentInvite,
      0
    )

    const hasAvailableSpots = totalAvailableSpots - totalOfferedSpots > 0

    const noFieldsLeft = inviteRows.length > 0 && !hasAvailableSpots

    const isOverCapacity =
      totalOfferedSpots > totalAvailableSpots ||
      spotStats.some(
        (capacity) =>
          capacity.spotsOfferedInCurrentInvite > capacity.availableSpots
      )

    return {
      spotStats,
      totalAvailableSpots,
      totalOfferedSpots,
      hasAvailableSpots,
      noFieldsLeft,
      isOverCapacity,
    }
  }, [capacityByProgram, inviteRows.length])

  const updateInviteRow = useCallback(
    (fieldId: string, update: InviteRowUpdate) => {
      setInviteRows((prev) =>
        prev.map((inviteRow) =>
          inviteRow.id === fieldId
            ? {
                ...inviteRow,
                [update.field]: update.value,
                isProgramFull: false,
              }
            : inviteRow
        )
      )
    },
    []
  )

  const addInviteRow = useCallback(() => {
    const availableProgram = programs.find((program) => {
      const capacity = capacityByProgram.get(program.programKey)

      return capacity && !capacity.outOfSpots
    })

    const newField: InviteRow = {
      id: createInviteRowId(),
      programKey: availableProgram?.programKey ?? null,
      previousProgramType: null,
      isApplicationFeePaid: false,
      isProgramFull: false,
    }

    setInviteRows((prev) => [...prev, newField])
  }, [programs, capacityByProgram])

  const removeInviteRow = useCallback((fieldId: string) => {
    setInviteRows((prev) => prev.filter((field) => field.id !== fieldId))
  }, [])

  const allValid = useMemo(() => {
    if (inviteRows.length === 0) return false

    return inviteRows.every(
      (field) => field.programKey != null && field.isApplicationFeePaid
    )
  }, [inviteRows])

  const handleCancel = () => onCancel()

  const handleSubmit = () => {
    if (!allValid) return

    const activeProgramOffers: Array<ProgramData> = Array.from(
      capacityByProgram.values()
    )
      .filter((capacity) => capacity.spotsOfferedInCurrentInvite > 0)
      .map((capacity) => ({
        programKey: capacity.programKey,
        offeredSpots: capacity.spotsOfferedInCurrentInvite,
        programType: capacity.programType,
        dateProgram: capacity.dateProgram,
      }))

    onSubmit({ programs: activeProgramOffers })
  }

  const formatProgramLabel = (
    program: ProgramInviteFamilyOptionsResponsePrograms
  ) => {
    const date = reinterpretYearMonthDayAsLocalTime(
      program.semesterOneStartDate
    )

    return `${program.programType} (${dateToDashString(date)})`
  }

  const isProgramDisabledForSelector = (
    program: ProgramInviteFamilyOptionsResponsePrograms,
    currentFieldProgramKey: number | null
  ) => {
    if (program.programKey === currentFieldProgramKey) {
      return false
    }

    const capacity = capacityByProgram.get(program.programKey)

    return capacity?.outOfSpots ?? true
  }

  const isSelectorGloballyDisabled =
    programs.length === 1 || !capacityStats.hasAvailableSpots

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <FormCard component={Paper}>
      <section aria-labelledby="inviteReturningFamilyHeader">
        <CardFormHeader
          header={
            <Header
              id="inviteReturningFamilyHeader"
              headerName={t(
                'Families.FamilyForm.ReinviteReturningFamily',
                'Re-invite a family'
              )}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
          buttons={
            showOnDesktop ? (
              <>
                <TextButton
                  id="cancel"
                  onClick={handleCancel}
                  variant={TextButtonVariant.Cancel}
                />

                <ContainedButton
                  id="sendInvitation"
                  variant={ContainedButtonVariant.SendInvitation}
                  onClick={handleSubmit}
                  disabled={
                    !allValid ||
                    capacityStats.isOverCapacity ||
                    disableSendButton
                  }
                />
              </>
            ) : null
          }
          headerContainerProps={{ margin: 0 }}
        />

        <Form>
          <SectionOne>
            <ReadOnlyTextField
              id="parentFirstName"
              label={t(
                'Families.FamilyForm.FormField.ParentFirstName',
                'Parent First Name'
              )}
              variant="filled"
              value={parentFirstName}
              disabled
              fullWidth
            />

            <ReadOnlyTextField
              id="parentLastName"
              label={t(
                'Families.FamilyForm.FormField.ParentLastName',
                'Parent Last Name'
              )}
              variant="filled"
              value={parentLastName}
              disabled
              fullWidth
            />

            <ReadOnlyTextField
              id="parentEmail"
              label={t(
                'Families.FamilyForm.FormField.ParentEmailAddress',
                'Parent Email Address'
              )}
              variant="filled"
              value={parentEmail}
              disabled
              fullWidth
            />

            <ReadOnlyTextField
              id="community"
              label={t('Families.FamilyForm.FormField.Community', 'Community')}
              variant="filled"
              value={communityName}
              disabled
              fullWidth
            />
          </SectionOne>

          {inviteRows.length > 0 && programs.length > 0 && (
            <InviteFamilyProgramCapacityContentCard
              availableSpots={capacityStats.spotStats}
              showOnDesktop={showOnDesktop}
            />
          )}

          <FormDivider />

          <Header
            id="studentInvitations"
            headerName={t(
              'Families.FamilyForm.StudentInvitations',
              'Student Invitations'
            )}
            component="h3"
            variant={HeaderVariant.Card}
          />

          <SectionTwo aria-labelledby="studentInvitations">
            {inviteRows.length > 0 && (
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  alignItems: 'center',
                  [theme.breakpoints.down('sm')]: {
                    display: 'none',
                  },
                }}
              >
                <Box sx={{ width: 48, flexShrink: 0 }} />

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    width: '25%',
                    pl: 2.5,
                  }}
                >
                  {t('Families.FamilyForm.PreviousProgram', 'Previous Program')}
                </Typography>

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    width: '25%',
                    mr: 2,
                    px: 1,
                  }}
                >
                  {t('Families.FamilyForm.ProgramInvite', 'Program Invite')}
                </Typography>

                <Box />
              </Box>
            )}

            {inviteRows.map((field) => (
              <StudentInviteRow
                key={field.id}
                field={field}
                programs={programs}
                isSelectorGloballyDisabled={isSelectorGloballyDisabled}
                formatProgramLabel={formatProgramLabel}
                isProgramDisabledForSelector={isProgramDisabledForSelector}
                onUpdate={updateInviteRow}
                onRemove={removeInviteRow}
              />
            ))}

            <Box
              sx={{
                padding: theme.spacing(1, 0),
                [theme.breakpoints.down('sm')]: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: theme.spacing(1, 0),
                },
              }}
            >
              <IconTextButton
                id="addStudentAndProgramButton"
                variant={IconTextButtonVariant.StudentAndProgram}
                startIcon={
                  <AddCircleIcon style={{ height: '32px', width: '32px' }} />
                }
                onClick={addInviteRow}
                disabled={
                  capacityStats.noFieldsLeft ||
                  capacityStats.totalAvailableSpots === 0
                }
              />
            </Box>
          </SectionTwo>

          {!showOnDesktop ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: theme.spacing(14),
              }}
            >
              <ContainedButton
                id="sendInvitationMobile"
                variant={ContainedButtonVariant.SendInvitation}
                onClick={handleSubmit}
                disabled={
                  !allValid || capacityStats.isOverCapacity || disableSendButton
                }
              />

              <TextButton
                id="cancelMobile"
                onClick={handleCancel}
                variant={TextButtonVariant.Cancel}
              />
            </Box>
          ) : null}
        </Form>
      </section>
    </FormCard>
  )
}
